import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "../Navbar/Navbar";
import "../Assets/Css/Table.css";
import Swal from "sweetalert2";
import { useAuth } from "../AuthProvider";
import { axiosConfig } from "../axiosAPI";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const trimValue = (value) => {
  const text = String(value ?? "").trim();
  return text || "—";
};

const hasText = (value) => String(value ?? "").trim().length > 0;

function PassengerDetails() {
  const { uuid: routePassengerUuid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const [passenger, setPassenger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: "", title: "" });

  const returnTo = location.state?.returnTo || "/User";

  useEffect(() => {
    const item = location.state?.item;
    const itemIsThisPassenger =
      item && String(item.uuid) === String(routePassengerUuid);

    if (itemIsThisPassenger) {
      setPassenger(item);
    }

    if (!routePassengerUuid || !auth.token) {
      if (!itemIsThisPassenger) {
        setPassenger(null);
      }
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await axiosConfig.get(
          `${process.env.REACT_APP_BASE_URL}/users/passengers/${routePassengerUuid}`,
          {
            headers: {
              Authorization: "Bearer " + auth.token,
              "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
            },
          }
        );
        if (!cancelled) {
          setPassenger(response.data.data ?? null);
        }
      } catch (error) {
        if (!cancelled) {
          if (!itemIsThisPassenger) {
            setPassenger(null);
          }
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Failed to load passenger profile!",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.state?.item, routePassengerUuid, auth.token]);

  const goBackToUsers = () => {
    if (location.state?.returnTo) {
      navigate(returnTo, location.state.returnState ? { state: location.state.returnState } : undefined);
    } else {
      navigate(-1);
    }
  };

  const handleImageClick = (imagePath, title) => {
    if (imagePath) {
      setSelectedImage({
        url: `${process.env.REACT_APP_BASE_URL_IMG}${imagePath}`,
        title,
      });
      setImageModalVisible(true);
    }
  };

  const ImageThumbnail = ({ imagePath, title, altText }) => {
    const imageUrl = imagePath
      ? `${process.env.REACT_APP_BASE_URL_IMG}${imagePath}`
      : null;

    return (
      <div className="image-thumbnail-container">
        <div
          className="image-thumbnail-wrapper"
          onClick={() => handleImageClick(imagePath, title)}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={altText || title}
              className="image-thumbnail"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className="image-placeholder"
            style={{ display: imageUrl ? "none" : "flex" }}
          >
            <i className="fa fa-image"></i>
            <span>No Image</span>
          </div>
        </div>
        <p className="image-title">{title}</p>
      </div>
    );
  };

  const InfoField = ({ label, value, children }) => (
    <div className="passenger-info-field">
      <span className="passenger-info-label">{label}</span>
      <span className="passenger-info-value">{children ?? trimValue(value)}</span>
    </div>
  );

  const stateClass =
    passenger?.stateLocalized?.toLowerCase() === "enabled"
      ? "passenger-status-badge passenger-status-badge--enabled"
      : "passenger-status-badge passenger-status-badge--disabled";

  const dogs = passenger?.dogs ?? [];

  const hasNationalIdNumber = hasText(passenger?.nationalID);
  const hasFrontNationalId = hasText(passenger?.frontNationalIDPath);
  const hasBackNationalId = hasText(passenger?.backNationalIDPath);
  const hasAnyNationalIdData =
    hasNationalIdNumber || hasFrontNationalId || hasBackNationalId;
  const isNationalIdComplete = Boolean(passenger?.nationalIDComplete);

  const NationalIdPartialState = () => (
    <>
      <div className="passenger-info-grid">
        <InfoField
          label="National ID Number"
          value={hasNationalIdNumber ? passenger.nationalID : null}
        >
          {!hasNationalIdNumber ? (
            <span className="passenger-missing-value">Not provided</span>
          ) : null}
        </InfoField>
      </div>

      <div className="row mt-3">
        {hasFrontNationalId ? (
          <div className="col-md-6 col-lg-4 mb-4">
            <ImageThumbnail
              imagePath={passenger.frontNationalIDPath}
              title="Front National ID"
              altText="Front National ID Document"
            />
          </div>
        ) : (
          <div className="col-md-6 col-lg-4 mb-4">
            <div className="passenger-document-missing">
              <span>Front National ID</span>
              <small>Not uploaded</small>
            </div>
          </div>
        )}
        {hasBackNationalId ? (
          <div className="col-md-6 col-lg-4 mb-4">
            <ImageThumbnail
              imagePath={passenger.backNationalIDPath}
              title="Back National ID"
              altText="Back National ID Document"
            />
          </div>
        ) : (
          <div className="col-md-6 col-lg-4 mb-4">
            <div className="passenger-document-missing">
              <span>Back National ID</span>
              <small>Not uploaded</small>
            </div>
          </div>
        )}
      </div>
    </>
  );

  const NationalIdCompleteState = () => (
    <>
      <div className="passenger-info-grid">
        <InfoField label="National ID Number" value={passenger.nationalID} />
      </div>
      <div className="row mt-3">
        <div className="col-md-6 col-lg-4 mb-4">
          <ImageThumbnail
            imagePath={passenger.frontNationalIDPath}
            title="Front National ID"
            altText="Front National ID Document"
          />
        </div>
        <div className="col-md-6 col-lg-4 mb-4">
          <ImageThumbnail
            imagePath={passenger.backNationalIDPath}
            title="Back National ID"
            altText="Back National ID Document"
          />
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="main">
        <div className="sidebar">
          <Sidebar />
        </div>
        <div className="dev-table">
          <Navbar />
          <div className="table-card">
            <div className="details passenger-details">
              <div className="row">
                <div className="col-md-12">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
                    <div className="d-flex align-items-center gap-3">
                      <a
                        className="edit"
                        href={returnTo}
                        onClick={(e) => {
                          e.preventDefault();
                          goBackToUsers();
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <i className="fa fa-arrow-left me-1"></i>
                        Back
                      </a>
                      <h1 className="mb-0">Passenger Profile</h1>
                    </div>
                    {passenger?.stateLocalized ? (
                      <span className={stateClass}>{passenger.stateLocalized}</span>
                    ) : null}
                  </div>
                </div>

                {loading ? (
                  <div className="col-md-12">
                    <p className="passenger-details-empty">Loading profile...</p>
                  </div>
                ) : !passenger ? (
                  <div className="col-md-12">
                    <p className="passenger-details-empty">Passenger not found.</p>
                  </div>
                ) : (
                  <>
                    <div className="col-md-12">
                      <div className="passenger-profile-card">
                        <div className="passenger-profile-card__header">
                          <div className="passenger-profile-avatar">
                            <i className="fa fa-user"></i>
                          </div>
                          <div>
                            <h2 className="passenger-profile-name">
                              {trimValue(passenger.fullName)}
                            </h2>
                            <p className="passenger-profile-subtitle">
                              {trimValue(passenger.userType || "Passenger")}
                            </p>
                          </div>
                        </div>

                        <div className="passenger-info-grid">
                          <InfoField label="Phone Number" value={passenger.phoneNumber} />
                          <InfoField label="Email" value={passenger.email} />
                          <InfoField
                            label="Phone Verified"
                            value={passenger.phoneNumberConfirmed ? "Yes" : "No"}
                          />
                          <InfoField label="Date Joined" value={formatDateTime(passenger.dateCreated)} />
                          <InfoField
                            label="Last Login"
                            value={formatDateTime(passenger.dateLastLogin)}
                          />
                          <InfoField label="User ID" value={passenger.uuid} />
                        </div>
                      </div>
                    </div>

                    <div className="col-md-12 mt-4">
                      <div className="passenger-section-card">
                        <h2 className="passenger-section-title">
                          <i className="fa fa-paw me-2"></i>
                          Dogs ({dogs.length})
                        </h2>
                        {dogs.length === 0 ? (
                          <p className="passenger-details-empty">No dogs registered.</p>
                        ) : (
                          <div className="passenger-dogs-grid">
                            {dogs.map((dog) => (
                              <div key={dog.uuid} className="passenger-dog-card">
                                <div className="passenger-dog-card__photo">
                                  {dog.photoPath ? (
                                    <img
                                      src={`${process.env.REACT_APP_BASE_URL_IMG}${dog.photoPath}`}
                                      alt={trimValue(dog.name)}
                                      onClick={() =>
                                        handleImageClick(dog.photoPath, trimValue(dog.name))
                                      }
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                      }}
                                    />
                                  ) : (
                                    <div className="passenger-dog-card__photo-placeholder">
                                      <i className="fa fa-paw"></i>
                                    </div>
                                  )}
                                </div>
                                <div className="passenger-dog-card__body">
                                  <h3>{trimValue(dog.name)}</h3>
                                  <div className="passenger-dog-meta">
                                    <span>
                                      <strong>Breed:</strong> {trimValue(dog.breedDisplayName)}
                                    </span>
                                    <span>
                                      <strong>Age:</strong> {dog.age ?? "—"} years
                                    </span>
                                    <span>
                                      <strong>Weight:</strong> {dog.weight ?? "—"} kg
                                    </span>
                                    <span>
                                      <strong>Gender:</strong> {trimValue(dog.gender)}
                                    </span>
                                    <span>
                                      <strong>License:</strong> {trimValue(dog.license)}
                                    </span>
                                    <span>
                                      <strong>Added:</strong> {formatDateTime(dog.dateCreated)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-12 mt-4">
                      <div className="passenger-section-card">
                        <div className="passenger-section-card__header">
                          <h2 className="passenger-section-title mb-0">
                            <i className="fa fa-id-card me-2"></i>
                            National ID
                          </h2>
                          {hasAnyNationalIdData ? (
                            <span
                              className={
                                isNationalIdComplete
                                  ? "passenger-tag passenger-tag--success"
                                  : "passenger-tag passenger-tag--warning"
                              }
                            >
                              {isNationalIdComplete ? "Complete" : "Incomplete"}
                            </span>
                          ) : (
                            <span className="passenger-tag passenger-tag--neutral">Not submitted</span>
                          )}
                        </div>

                        {!hasAnyNationalIdData ? (
                          <p className="passenger-details-empty mb-0">No national ID on file.</p>
                        ) : isNationalIdComplete ? (
                          <NationalIdCompleteState />
                        ) : (
                          <NationalIdPartialState />
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        visible={imageModalVisible}
        modal
        header={selectedImage.title}
        style={{ width: "90vw", maxWidth: "800px" }}
        onHide={() => setImageModalVisible(false)}
        footer={
          <div className="modal-footer">
            <Button
              label="Close"
              icon="pi pi-times"
              onClick={() => setImageModalVisible(false)}
              className="p-button-text"
            />
          </div>
        }
      >
        <div className="image-modal-content">
          <img
            src={selectedImage.url}
            alt={selectedImage.title}
            className="modal-image img-fluid"
            style={{ maxHeight: "70vh", width: "100%", objectFit: "contain" }}
          />
        </div>
      </Dialog>
    </>
  );
}

export default PassengerDetails;
